import { useRef, useState, useEffect } from "react";
import { api, extractError } from "@/lib/api";
import { useLogo } from "@/lib/logo";
import { useAdminAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Image as ImageIcon, ShieldCheck, User, Palette, Users, AlertTriangle, Trash2, MessageSquare, Copy } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AdminUsuarios from "./AdminUsuarios";

export default function AdminConfig() {
  const { admin } = useAdminAuth();
  const { logoUrl, invalidate } = useLogo();
  const fileRef = useRef(null);
  const isMaster = admin?.role === "ADMIN_MASTER";

  // Reset production state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1=aviso, 2=digitar APAGAR
  const [resetInput, setResetInput] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Template de mensagem de cobrança
  const [msgTemplate, setMsgTemplate] = useState("");
  const [msgDefault, setMsgDefault] = useState("");
  const [msgTags, setMsgTags] = useState([]);
  const [msgSaving, setMsgSaving] = useState(false);

  // Carrega template ao montar
  const loadTemplate = async () => {
    try {
      const { data } = await api.get("/config/mensagem-template");
      setMsgTemplate(data.template || "");
      setMsgDefault(data.default || "");
      setMsgTags(data.tags_disponiveis || []);
    } catch (e) {
      // ignora — pode ser primeiro load
    }
  };

  const salvarTemplate = async () => {
    setMsgSaving(true);
    try {
      await api.put("/config/mensagem-template", { template: msgTemplate });
      toast.success("Modelo de mensagem salvo");
    } catch (e) {
      toast.error(extractError(e) || "Falha ao salvar modelo");
    } finally {
      setMsgSaving(false);
    }
  };

  const insertTag = (tag) => {
    setMsgTemplate((t) => t + tag);
  };

  useEffect(() => { loadTemplate(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/config/logo", fd);
      toast.success("Logo atualizada — recarregando");
      invalidate();
      window.location.reload();
    } catch (e) {
      toast.error(extractError(e) || "Falha no upload");
    }
  };

  const executarReset = async () => {
    if (resetInput !== "APAGAR") {
      toast.error('Digite exatamente "APAGAR" para confirmar');
      return;
    }
    setResetLoading(true);
    try {
      const { data } = await api.post("/config/reset-production", { confirmation: "APAGAR" });
      setResetResult(data.resultados);
      toast.success("Sistema zerado para produção");
    } catch (e) {
      toast.error(extractError(e) || "Falha ao zerar sistema");
    } finally {
      setResetLoading(false);
    }
  };

  const fecharModalReset = () => {
    setResetOpen(false);
    setResetStep(1);
    setResetInput("");
    setResetResult(null);
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="label-tag mb-2">Personalização · Usuários · Perfis</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Configurações</h1>
      </div>

      <Tabs defaultValue="marca">
        <TabsList className="rounded-sm">
          <TabsTrigger value="marca" data-testid="cfg-tab-marca"><Palette size={12} className="mr-2" />Marca</TabsTrigger>
          <TabsTrigger value="comunicacoes" data-testid="cfg-tab-comunicacoes"><MessageSquare size={12} className="mr-2" />Comunicações</TabsTrigger>
          {isMaster && <TabsTrigger value="usuarios" data-testid="cfg-tab-usuarios"><Users size={12} className="mr-2" />Usuários</TabsTrigger>}
          <TabsTrigger value="perfil" data-testid="cfg-tab-perfil"><User size={12} className="mr-2" />Perfil</TabsTrigger>
          <TabsTrigger value="hierarquia" data-testid="cfg-tab-roles"><ShieldCheck size={12} className="mr-2" />Hierarquia</TabsTrigger>
          {isMaster && <TabsTrigger value="sistema" data-testid="cfg-tab-sistema"><AlertTriangle size={12} className="mr-2" />Sistema</TabsTrigger>}
        </TabsList>

        <TabsContent value="marca" className="mt-6">
          <Card className="p-6 rounded-sm border-border max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <ImageIcon size={18} />
              <h2 className="font-heading font-bold text-xl">Logotipo do Shopping</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              A logo aparece no cabeçalho de Admin + Portal do Lojista + Landing.
              PNG, JPG, WEBP, SVG · até 5MB.
            </p>

            <div className="border border-border rounded-sm p-8 flex items-center justify-center bg-secondary/40 mb-4 min-h-[140px]">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo atual" className="max-h-24 max-w-full" />
              ) : (
                <div className="text-center text-muted-foreground">
                  <ImageIcon size={32} className="mx-auto opacity-40 mb-2" />
                  <div className="text-xs">Nenhum logo enviado</div>
                </div>
              )}
            </div>

            {isMaster ? (
              <div className="space-y-3">
                <Input data-testid="config-logo-file" ref={fileRef} type="file" accept="image/*" className="rounded-sm" />
                <Button data-testid="config-logo-upload" onClick={upload} className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90">
                  <Upload size={14} className="mr-2" /> Enviar novo logo
                </Button>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground text-center py-2 border border-dashed rounded-sm">
                Somente Admin Master pode alterar o logo.
              </div>
            )}
          </Card>
        </TabsContent>

        {isMaster && (
          <TabsContent value="usuarios" className="mt-6">
            <AdminUsuarios />
          </TabsContent>
        )}

        <TabsContent value="comunicacoes" className="mt-6">
          <Card className="p-6 rounded-sm border-border max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare size={18} />
              <h2 className="font-heading font-bold text-xl">Modelo de Mensagem de Cobrança</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Personalize o texto que os lojistas receberão por WhatsApp e E-mail. Use as tags abaixo — elas serão
              substituídas automaticamente pelos dados reais do boleto no momento do envio.
            </p>

            <div className="flex flex-wrap gap-2 mb-3">
              {msgTags.map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant="outline"
                  onClick={() => insertTag(t)}
                  className="rounded-sm font-mono text-[11px] h-7"
                  data-testid={`cfg-tag-${t.replace(/[{}]/g, "").toLowerCase()}`}
                  title={`Inserir ${t}`}
                >
                  <Copy size={10} className="mr-1" /> {t}
                </Button>
              ))}
            </div>

            <Textarea
              data-testid="cfg-msg-template"
              value={msgTemplate}
              onChange={(e) => setMsgTemplate(e.target.value)}
              rows={12}
              className="font-mono text-xs rounded-sm"
              placeholder="Digite ou cole o modelo aqui..."
            />
            <div className="text-[10px] text-muted-foreground mt-2">
              {msgTemplate.length} / 4000 caracteres
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                data-testid="cfg-msg-salvar"
                onClick={salvarTemplate}
                disabled={msgSaving || !msgTemplate.trim()}
                className="bg-foreground text-background hover:bg-foreground/90 rounded-sm"
              >
                {msgSaving ? "Salvando..." : "Salvar modelo"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setMsgTemplate(msgDefault)}
                className="rounded-sm"
                data-testid="cfg-msg-restaurar"
              >
                Restaurar padrão
              </Button>
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <div className="label-tag mb-2">Prévia (com dados de exemplo)</div>
              <div className="text-xs whitespace-pre-wrap bg-muted/50 border border-border rounded-sm p-3 leading-relaxed">
                {msgTemplate
                  .replace(/\{NOME\}/g, "Carlos Souza")
                  .replace(/\{LOJA\}/g, "LOJA-20 (Kaven Restaurante)")
                  .replace(/\{VALOR\}/g, "13.762,29")
                  .replace(/\{VENCIMENTO\}/g, "10/08/2026")
                  .replace(/\{LINK_PORTAL\}/g, "https://appvmshopping.com.br/lojista/dashboard")
                  .replace(/\{PORTAL\}/g, "https://appvmshopping.com.br/lojista/dashboard")
                  .replace(/\{COMPETENCIA\}/g, "2026-07")
                  // Compatibilidade com templates antigos — tags descontinuadas
                  // são substituídas pelo link do portal para não vazar payload.
                  .replace(/\{PIX_COPIA_COLA\}/g, "https://appvmshopping.com.br/lojista/dashboard")
                  .replace(/\{LINK_BOLETO\}/g, "https://appvmshopping.com.br/lojista/dashboard")
                }
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="perfil" className="mt-6">
          <Card className="p-6 rounded-sm border-border max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <User size={18} />
              <h2 className="font-heading font-bold text-xl">Sua conta</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="label-tag mb-1">Nome</div>
                <div>{admin?.name}</div>
              </div>
              <div>
                <div className="label-tag mb-1">E-mail</div>
                <div className="font-mono break-anywhere">{admin?.email}</div>
              </div>
              <div>
                <div className="label-tag mb-1">Perfil</div>
                <Badge className="uppercase tracking-widest text-[10px]">{admin?.role}</Badge>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="hierarquia" className="mt-6">
          <Card className="p-6 rounded-sm border-border max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={18} />
              <h2 className="font-heading font-bold text-xl">Níveis de acesso</h2>
            </div>
            <div className="space-y-3">
              {[
                { r: "ADMIN_MASTER", l: "Admin Master", d: "Acesso irrestrito · Financeiro · DP · Configurações · Cadastro de usuários · Exclusões" },
                { r: "GERENCIA", l: "Gerência", d: "Operacional + Financeiro + DP · Visualização e edição · Sem exclusões master, sem gestão de usuários" },
                { r: "OPERACIONAL", l: "Operacional", d: "Chamados de manutenção · Dashboard · Unidades (leitura) · Portaria/Prestadores" },
              ].map((x) => (
                <div key={x.r} className={`border p-4 rounded-sm ${admin?.role === x.r ? "border-foreground bg-accent" : "border-border"}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-semibold text-sm">{x.l}</span>
                    {admin?.role === x.r && <Badge className="text-[9px] uppercase tracking-widest">Seu perfil</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 break-anywhere">{x.d}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {isMaster && (
          <TabsContent value="sistema" className="mt-6">
            <Card className="p-6 rounded-sm border-2 border-destructive/40 bg-destructive/5 max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle size={20} className="text-destructive" />
                <h2 className="font-heading font-bold text-xl text-destructive">Zona de perigo</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                O botão abaixo <b>zera totalmente o sistema para produção</b>. Use apenas quando o VM Shopping
                estiver pronto para começar a operação real e você precisar apagar todos os dados de teste.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside mb-6">
                <li><b>Apaga:</b> todos os contratos, boletos, pagamentos, despesas, obras, notificações, logs de WhatsApp/E-mail, documentos e histórico de unidades</li>
                <li><b>Reseta:</b> todas as 65 unidades voltam para status <b>LIVRE</b></li>
                <li><b>Preserva:</b> usuários administrativos, logo, configuração IPTU do terreno e as próprias unidades cadastradas</li>
              </ul>
              <Button
                data-testid="btn-reset-production"
                onClick={() => setResetOpen(true)}
                className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 size={14} className="mr-2" />
                Zerar Sistema para Produção
              </Button>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Modal de dupla confirmação — Reset Produção */}
      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) fecharModalReset(); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="modal-reset-production">
          {!resetResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle size={18} />
                  {resetStep === 1 ? "Confirmar Reset de Fábrica" : "Digite a palavra de confirmação"}
                </DialogTitle>
              </DialogHeader>
              {resetStep === 1 ? (
                <div className="space-y-4">
                  <p className="text-sm leading-relaxed">
                    Você está prestes a <b>apagar todos os dados operacionais</b> do sistema.
                    Esta ação é <b>irreversível</b>.
                  </p>
                  <div className="border border-warning/40 bg-warning/5 p-3 rounded-sm text-xs">
                    <div className="label-tag mb-2 text-warning">O que será removido:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Todos os contratos e boletos</li>
                      <li>Histórico de pagamentos e despesas</li>
                      <li>Comunicações (WhatsApp, e-mail)</li>
                      <li>Obras, solicitações e notificações</li>
                      <li>Todas as 65 unidades voltam para <b>LIVRE</b></li>
                    </ul>
                  </div>
                  <div className="border border-success/40 bg-success/5 p-3 rounded-sm text-xs">
                    <div className="label-tag mb-2 text-success">O que será preservado:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Usuários administrativos (roles)</li>
                      <li>Logo e branding</li>
                      <li>Parametrização base (IPTU do terreno)</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm leading-relaxed">
                    Para confirmar definitivamente, digite <b className="font-mono text-destructive">APAGAR</b> no campo abaixo:
                  </p>
                  <Input
                    data-testid="reset-confirmar-input"
                    value={resetInput}
                    onChange={(e) => setResetInput(e.target.value.toUpperCase())}
                    placeholder="APAGAR"
                    className="rounded-sm font-mono text-center text-lg tracking-widest"
                    autoFocus
                  />
                  <p className="text-xs text-destructive">
                    Confirmação sensível a maiúsculas — digite exatamente APAGAR.
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={fecharModalReset} disabled={resetLoading}>
                  Cancelar
                </Button>
                {resetStep === 1 ? (
                  <Button
                    data-testid="reset-avancar"
                    onClick={() => setResetStep(2)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Entendo os riscos, prosseguir
                  </Button>
                ) : (
                  <Button
                    data-testid="reset-executar"
                    onClick={executarReset}
                    disabled={resetLoading || resetInput !== "APAGAR"}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    <Trash2 size={14} className="mr-2" />
                    {resetLoading ? "Zerando..." : "Executar reset agora"}
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-success flex items-center gap-2">
                  <ShieldCheck size={18} /> Sistema zerado com sucesso
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm font-mono">
                {Object.entries(resetResult).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border/60 py-1">
                    <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                    <span className="font-bold">{v}</span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button
                  onClick={() => { fecharModalReset(); window.location.reload(); }}
                  className="bg-foreground text-background hover:bg-foreground/90"
                  data-testid="reset-fechar"
                >
                  Recarregar sistema
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
